import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { CreateTVGuideRequest } from '@/types/model';
import { useTranslation } from '@/hooks';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';

const createTVGuideSchema = (t: Translation) => z.object({
  title: z
    .string()
    .min(3, t.validation.minLength(3))
    .max(255, t.validation.maxLength(255)),
  url: z
    .url({
      message: t.tvGuide.validation.urlInvalid
    }),
  active: z.boolean(),
  gzip: z.boolean()
});

type TVGuideFormData = z.infer<ReturnType<typeof createTVGuideSchema>>;

interface TVGuideFormProps {
  onSuccess?: (data: CreateTVGuideRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateTVGuideRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const TVGuideForm = ({
  onSuccess,
  onCancel,
  initialData,
  isEdit = false,
  isSubmitting = false
}: TVGuideFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<TVGuideFormData>({
    resolver: zodResolver(createTVGuideSchema(t)),
    defaultValues: {
      title: initialData?.title || '',
      url: initialData?.url || '',
      active: initialData?.active ?? true,
      gzip: initialData?.gzip ?? false
    }
  });

  const onSubmit = async (data: TVGuideFormData) => {
    setSubmitError(null);

    try {
      await onSuccess?.(data);
      if (!isEdit) {
        reset();
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const activeValue = watch('active');
  const gzipValue = watch('gzip');

  return (
    <div className="bg-white rounded-none p-6 border border-gray-200">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEdit ? t.tvGuide.editTitle : t.tvGuide.createTitle}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEdit ? t.tvGuide.editDescription : t.tvGuide.createDescription}
        </p>
      </div>

      {submitError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Input
              label={t.tvGuide.fields.title}
              placeholder={t.tvGuide.placeholders.title}
              error={errors.title?.message}
              {...register('title')}
            />
          </div>

          <div className="md:col-span-2">
            <Input
              label={t.tvGuide.fields.url}
              type="url"
              placeholder={t.tvGuide.placeholders.url}
              error={errors.url?.message}
              helperText="Ejemplo: https://example.com/guide.xml"
              {...register('url')}
            />
          </div>

          <div className="flex items-center justify-between">
            <Toggle
              label={t.tvGuide.fields.active}
              checked={activeValue}
              onChange={(checked) => setValue('active', checked)}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-center justify-between">
            <Toggle
              label={t.tvGuide.fields.gzip}
              checked={gzipValue}
              onChange={(checked) => setValue('gzip', checked)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t.tvGuide.actions.creating
              : isEdit
                ? 'Actualizar Guía de TV'
                : t.tvGuide.actions.create
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TVGuideForm;
